import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { APIgetGroups } from '../../../api/Group';
import { APIgetUserGroups } from '../../../api/User';

import AdminEditButtons from '../EditButtons';
import AdminUserEditRoleSelect from './RoleSelect';
import AdminUserEditViewModeSwitcher from './ViewModeSwitcher';

import { isTeamDisabled } from '../../../helpers/Auth';

import ErrorBoundary from '../../ErrorBoundary';
import LoadingSmall from '../../LoadingSmall';

class AdminUserEditPermissions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      groups: [],
      groupsLoading: true,
    };
  }

  componentDidMount() {
    const { isSelfEdit } = this.props;

    if (isSelfEdit) {
      this.loadUserGroups();
    } else {
      this.loadGroups();
    }
  }

  loadGroups() {
    this.setState({ groupsLoading: true });

    APIgetGroups({ all: true }).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ groups: data });
      }
      this.setState({ groupsLoading: false });
    });
  }

  loadUserGroups() {
    const { profile } = this.props;

    this.setState({ groupsLoading: true });

    APIgetUserGroups(profile.uuid).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({ groups: data });
      }
      this.setState({ groupsLoading: false });
    });
  }

  isTeamChecked(teamUuid) {
    const { profile } = this.props;
    return profile.groups && profile.groups.find(pg => (pg.uuid === teamUuid));
  }

  render() {
    const {
      intl,
      profile,
      client,
      isSelfEdit,
      loggedInUser,
      viewModeSwitcher,
      handleChangeRole,
      handleSwitchGroup,
      handleSwitchViewModeSwitcher,
    } = this.props;
    const {
      groups,
      groupsLoading,
    } = this.state;

    const groupsToDisplay = groups.filter(g => (
      !isTeamDisabled(g.uuid) || this.isTeamChecked(g.uuid)
    ));

    return (
      <ErrorBoundary>
        <div className="tab-content-wrapper">
          <h4>
            {
              isSelfEdit ?
                intl.formatMessage({ defaultMessage: 'Your Role' }) :
                intl.formatMessage({ defaultMessage: 'What Role should this user have?' })
            }
          </h4>
          {(isSelfEdit && profile.is_superadmin) ? (
            <p>
              {intl.formatMessage({ defaultMessage: 'You are super admin. You cannot change your Role.' })}
            </p>
          ) : (
            <>
              {isSelfEdit ? (
                <p>
                  {intl.formatMessage({
                    defaultMessage: 'This is your Role. It assigns you the associated permissions.',
                  })}
                </p>
              ) : (
                <p>
                  {intl.formatMessage({
                    defaultMessage: `The Role you select will determine how they can use Stockpress. You can change the Role at any time.`,
                  })}
                </p>
              )}
            </>
          )}
          {(!(isSelfEdit && profile.is_superadmin)) && (
            <AdminUserEditRoleSelect
              profile={profile}
              isThisUserWorkspaceOwner={profile.uuid === client.workspace_owner?.uuid}
              isSelfEdit={isSelfEdit}
              handleChangeRole={handleChangeRole}
            />
          )}
          <AdminUserEditViewModeSwitcher
            profile={profile}
            isSelfEdit={isSelfEdit}
            loggedInUser={loggedInUser}
            viewModeSwitcher={viewModeSwitcher}
            handleSwitchViewModeSwitcher={handleSwitchViewModeSwitcher}
          />
          <hr />
          {!profile.is_superadmin && (
            <>
              <h4>
                {
                  isSelfEdit ?
                    intl.formatMessage({ defaultMessage: 'Your Teams' }) :
                    intl.formatMessage({ defaultMessage: 'What Team(s) should this user belong to?' })
                }
              </h4>
              {isSelfEdit ? (
                <p>
                  {intl.formatMessage({ defaultMessage: 'These are Teams you are assigned to.' })}
                </p>
              ) : (
                <p>
                  {intl.formatMessage({
                    defaultMessage: `This user will have immediate access to all files and
                    Collections that these Team(s) have access to.
                    You can manage Teams <link>here</link>.`,
                  }, {
                    link: (msg) => (
                      <Link to="/admin/teams">{msg}</Link>
                    ),
                  })}
                </p>
              )}
              <FormGroup>
                {groupsLoading ? (
                  <LoadingSmall />
                ) : (
                  <ListGroup flush className="switch-list group-permissions-list files-permissions-list user-profile-groups-list">
                    {groupsToDisplay.map(g => (
                      <ListGroupItem key={g.uuid}>
                        <Label for={`switchGroup-${g.uuid}`} className={classnames('switch', { disabled: isTeamDisabled(g.uuid) })}>
                          <div className="switch-details">
                            <span>
                              {g.name}
                            </span>
                          </div>
                          {!isSelfEdit && (
                            <div className="switch-control input-inner">
                              <Input
                                type="checkbox"
                                name="groupUploadFile"
                                id={`switchGroup-${g.uuid}`}
                                value="true"
                                defaultChecked={this.isTeamChecked(g.uuid)}
                                disabled={isTeamDisabled(g.uuid)}
                                onChange={() => { handleSwitchGroup(g.uuid); }}
                              />
                              <i />
                            </div>
                          )}
                        </Label>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                )}
              </FormGroup>
            </>
          )}
        </div>
        <AdminEditButtons
          {...this.props}
          hideDelete={true}
        />
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
});

AdminUserEditPermissions.propTypes = {
  intl: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  client: PropTypes.object.isRequired,
  viewModeSwitcher: PropTypes.bool.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  isSelfEdit: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  handleChangeRole: PropTypes.func.isRequired,
  handleSwitchGroup: PropTypes.func.isRequired,
  handleSwitchViewModeSwitcher: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(injectIntl(AdminUserEditPermissions));
